import React from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import SwapVertIcon from 'components/material-symbol/icons/SwapVert'
import VideocamOutlinedIcon from 'components/material-symbol/icons/Videocam'
import PlayCircleIcon from 'components/material-symbol/icons/PlayCircle'
import SectionEmptyState from 'components/section-empty-state'
import { TonalButton } from 'components/empty-page-state'
import EnhancedTable from 'components/tables/enhanced-table'
import { getInitials } from 'lib/text-display'
import { useStudentListShellStyles } from 'pages/pga-coach/students/student-list-shell-styles'
import { useStudentListNameAgeStyles } from 'pages/pga-coach/students/student-list-name-age-styles'
import VideoThumbnail from './video-thumbnail'
import {
  formatDate,
  formatTurnaroundDuration,
  getAgingLabel,
  getRequestPath,
  getStudentName,
  getTurnaroundHours,
  isOverdue,
  REQUEST_TABS,
} from './utils'

// The table is the Students list table (`EnhancedTable` variant="list" inside
// the Students shell) so both lists share headers, row height, dividers and
// hover. Only the cells are Virtual Coaching's own.
const useStyles = makeStyles(theme => ({
  // The shared list header cells fall back to Roboto (and ask for a serif);
  // this app is Inter only.
  table: {
    '& th': { fontFamily: theme.typography.fontFamily },
  },
  golferMeta: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    lineHeight: '16px',
  },
  overdue: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  actions: {
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  // Same shape as the buttons in "Needs your attention", but hugging the
  // label (no fixed or minimum width).
  action: {
    minWidth: 0,
    height: 36,
    fontSize: '0.875rem',
    borderRadius: 20,
    fontWeight: 500,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  buttonLabel: { textTransform: 'none' },
}))

// Each tab shows the columns that make sense for it. Golfer carries the
// offering title and purchase date underneath, so no tab needs a separate
// purchased column.
const column = (id, label, extra) => ({
  id,
  label,
  align: 'left',
  mobile: true,
  sortable: false,
  ...extra,
})

// Sortable columns use the Students table mechanism: `EnhancedTable` sorts the
// row elements with `sortComparator`, reading `sortValues` off each row's props.
const compareSortValues = (a, b, direction, id) => {
  const first = a.props.sortValues[id]
  const second = b.props.sortValues[id]
  // Rows with no value (e.g. no due date yet) always sort last.
  if (first == null && second == null) return 0
  if (first == null) return 1
  if (second == null) return -1
  const result =
    typeof first === 'string'
      ? first.localeCompare(second)
      : first === second
        ? 0
        : first < second
          ? -1
          : 1
  return direction === 'desc' ? -result : result
}

const sortable = (id, label, extra) =>
  column(id, label, {
    sortable: true,
    sortComparator: (a, b, direction) => compareSortValues(a, b, direction, id),
    ...extra,
  })

const GOLFER = sortable('golfer', 'Golfer', { width: '40%' })
const ACTIONS = column('actions', '', { align: 'right', width: '16%' })

const COLUMNS = {
  [REQUEST_TABS.PURCHASED]: [GOLFER],
  [REQUEST_TABS.READY]: [
    GOLFER,
    column('video', 'Video'),
    sortable('aging', 'Time to deliver', { inactiveSortIcon: SwapVertIcon }),
    ACTIONS,
  ],
  [REQUEST_TABS.COMPLETE]: [
    GOLFER,
    column('analysis', 'Video'),
    sortable('completed', 'Delivered', { inactiveSortIcon: SwapVertIcon }),
    sortable('turnaround', 'Turnaround', { inactiveSortIcon: SwapVertIcon }),
    ACTIONS,
  ],
}

// Each tab opens sorted the way it reads best: most urgent first, newest
// delivery first.
const DEFAULT_SORT = {
  [REQUEST_TABS.PURCHASED]: { orderBy: 'golfer', order: 'asc' },
  [REQUEST_TABS.READY]: { orderBy: 'aging', order: 'asc' },
  [REQUEST_TABS.COMPLETE]: { orderBy: 'completed', order: 'desc' },
}

const toTime = isoString => {
  const time = isoString ? Date.parse(isoString) : NaN
  return Number.isNaN(time) ? null : time
}

const getSortValues = request => ({
  golfer: getStudentName(request.student).toLowerCase(),
  aging: toTime(request.dueAt),
  completed: toTime(request.completedAt || request.response?.sharedAt),
  turnaround: getTurnaroundHours(request),
})

// `sortValues` is read by the column comparators, not forwarded to the DOM.
// eslint-disable-next-line no-unused-vars
const RequestRow = ({ sortValues, ...props }) => <TableRow {...props} />

const ACTION_LABELS = {
  [REQUEST_TABS.READY]: 'Review',
  [REQUEST_TABS.COMPLETE]: 'View',
}

const EMPTY_CAPTIONS = {
  [REQUEST_TABS.PURCHASED]: 'No purchases waiting on a video from the golfer.',
  [REQUEST_TABS.READY]: 'No videos are waiting for your analysis.',
  [REQUEST_TABS.COMPLETE]: 'You have not completed any virtual coaching yet.',
}

// "Virtual Swing Analysis · purchased Oct 17, 2026"
const getGolferMeta = request =>
  [
    request.offering?.title,
    request.purchasedAt ? `purchased ${formatDate(request.purchasedAt)}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

const noop = () => {}

const RequestsTable = ({
  requests,
  tab,
  emptyTitle,
  emptyCaption,
  emptyAction,
  page = 0,
  rowsPerPage,
}) => {
  const classes = useStyles()
  const shellClasses = useStudentListShellStyles()
  const nameClasses = useStudentListNameAgeStyles()
  const history = useHistory()
  const columns = COLUMNS[tab]
  // Review is the primary action; View is the quieter tonal button.
  const ActionButton = tab === REQUEST_TABS.READY ? Button : TonalButton

  const openRequest = request => history.push(getRequestPath(request.id))

  const renderCell = (columnId, request) => {
    const golfer = getStudentName(request.student)
    const overdue = isOverdue(request)

    switch (columnId) {
      case 'golfer':
        return (
          <Box className={nameClasses.nameCell}>
            <Avatar className={nameClasses.studentAvatar}>
              {getInitials(golfer)}
            </Avatar>
            <Box className={nameClasses.info}>
              <Typography className={nameClasses.text} noWrap>
                {golfer}
              </Typography>
              <Typography className={classes.golferMeta} noWrap>
                {getGolferMeta(request)}
              </Typography>
            </Box>
          </Box>
        )
      case 'video':
        return (
          <VideoThumbnail
            src={request.videos?.[0]?.thumbnailUrl}
            alt={`Video from ${golfer}`}
          />
        )
      case 'aging':
        return (
          <Typography
            variant="body2"
            className={overdue ? classes.overdue : undefined}
          >
            {getAgingLabel(request)}
          </Typography>
        )
      case 'analysis':
        return (
          <VideoThumbnail
            src={request.response?.thumbnailUrl}
            alt={`Analysis for ${golfer}`}
          />
        )
      case 'completed':
        return (
          <Typography variant="body2">
            {formatDate(request.completedAt)}
          </Typography>
        )
      case 'turnaround':
        return (
          <Typography variant="body2">
            {formatTurnaroundDuration(getTurnaroundHours(request))}
          </Typography>
        )
      case 'actions':
        return (
          <ActionButton
            variant="contained"
            color={tab === REQUEST_TABS.READY ? 'primary' : 'default'}
            size="medium"
            className={classes.action}
            classes={{ label: classes.buttonLabel }}
            startIcon={
              tab === REQUEST_TABS.READY ? <PlayCircleIcon aria-hidden /> : null
            }
            onClick={event => {
              event.stopPropagation()
              openRequest(request)
            }}
          >
            {ACTION_LABELS[tab]}
          </ActionButton>
        )
      default:
        return null
    }
  }

  const rows = requests.map(request => (
    <RequestRow
      key={request.id}
      sortValues={getSortValues(request)}
      hover
      className={nameClasses.row}
      onClick={() => openRequest(request)}
    >
      {columns.map(({ id }) => (
        <TableCell
          key={id}
          align={id === 'actions' ? 'right' : 'left'}
          className={id === 'actions' ? classes.actions : undefined}
        >
          {renderCell(id, request)}
        </TableCell>
      ))}
    </RequestRow>
  ))

  return (
    <Box className={`${shellClasses.tableContent} ${classes.table}`}>
      <EnhancedTable
        key={tab}
        rows={rows}
        columns={columns}
        label="Virtual Coaching"
        variant="list"
        rowHeight="56px"
        showHeaderPagination={false}
        showPaginator={false}
        initialOrderBy={DEFAULT_SORT[tab].orderBy}
        initialOrder={DEFAULT_SORT[tab].order}
        // Controlled: the list page owns the page, and the table sorts every
        // request in the tab before slicing the page.
        numberOfRows={rowsPerPage}
        page={page}
        onPageChange={noop}
        emptyState={() => (
          <Box className={shellClasses.listEmptyStateContent}>
            <SectionEmptyState
              Icon={VideocamOutlinedIcon}
              title={emptyTitle || 'Virtual Coaching'}
              caption={emptyCaption || EMPTY_CAPTIONS[tab]}
              action={emptyAction}
              bordered={false}
            />
          </Box>
        )}
      />
    </Box>
  )
}

RequestsTable.propTypes = {
  requests: PropTypes.arrayOf(PropTypes.object).isRequired,
  tab: PropTypes.oneOf(Object.values(REQUEST_TABS)).isRequired,
  emptyTitle: PropTypes.string,
  emptyCaption: PropTypes.string,
  emptyAction: PropTypes.node,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
}

export default RequestsTable
